import { create } from 'zustand';
import { revenueApi } from '../services/revenue-api';
import type {
  RevenueByStudio,
  RevenueBreakdownItem,
  RevenueTrend,
  VoucherStats,
} from '../types/revenue';
import { format, startOfWeek, endOfWeek } from 'date-fns';

type TrendGroupBy = 'day' | 'week' | 'month';

interface RevenueState {
  fromDate: string;
  toDate: string;
  trendGroupBy: TrendGroupBy;

  revenueByStudio: RevenueByStudio[];
  revenueByProvince: RevenueBreakdownItem[];
  revenueByDealType: RevenueBreakdownItem[];
  revenueByPaymentMethod: RevenueBreakdownItem[];
  revenueBySales: RevenueBreakdownItem[];
  revenueByProductType: RevenueBreakdownItem[];
  voucherStats: VoucherStats | null;
  revenueTrend: RevenueTrend[];

  reportFrom: string;
  reportTo: string;
  fetchedKey: string | null;

  isLoading: boolean;
  error: string | null;

  setDateRange: (from: string, to: string) => void;
  setTrendGroupBy: (g: TrendGroupBy) => void;
  fetchDashboardData: (force?: boolean) => Promise<void>;
  fetchTrend: (groupBy: TrendGroupBy) => Promise<void>;
}

const now = new Date();
const thisMonday = startOfWeek(now, { weekStartsOn: 1 });
const lastMonday = new Date(thisMonday);
lastMonday.setDate(thisMonday.getDate() - 7);
const lastSunday = endOfWeek(lastMonday, { weekStartsOn: 1 });
const defaultFrom = format(lastMonday, 'yyyy-MM-dd');
const defaultTo = format(lastSunday, 'yyyy-MM-dd');

export const useRevenueStore = create<RevenueState>((set, get) => ({
  fromDate: defaultFrom,
  toDate: defaultTo,
  trendGroupBy: 'day' as TrendGroupBy,

  revenueByStudio: [],
  revenueByProvince: [],
  revenueByDealType: [],
  revenueByPaymentMethod: [],
  revenueBySales: [],
  revenueByProductType: [],
  voucherStats: null,
  revenueTrend: [],

  reportFrom: defaultFrom,
  reportTo: defaultTo,
  fetchedKey: null,

  isLoading: false,
  error: null,

  setDateRange: (from, to) => {
    set({ fromDate: from, toDate: to });
  },

  setTrendGroupBy: (g) => {
    set({ trendGroupBy: g });
  },

  fetchTrend: async (groupBy: TrendGroupBy) => {
    const { fromDate, toDate } = get();
    try {
      const revenueTrend = await revenueApi.getRevenueTrend({ from: fromDate, to: toDate, groupBy });
      set({ revenueTrend, trendGroupBy: groupBy });
    } catch (error: any) {
      set({ error: error.message || 'Failed to fetch trend data' });
    }
  },

  fetchDashboardData: async (force = false) => {
    const { fromDate, toDate, fetchedKey, trendGroupBy } = get();
    const currentKey = `${fromDate}|${toDate}`;

    if (!force && fetchedKey === currentKey) return;

    set({ isLoading: true, error: null });

    try {
      const params = { from: fromDate, to: toDate };
      const [
        revenueByStudio,
        revenueByProvince,
        revenueByDealType,
        revenueByPaymentMethod,
        revenueBySales,
        revenueByProductType,
        voucherStats,
        revenueTrend,
      ] = await Promise.all([
        revenueApi.getRevenueByStudio(params),
        revenueApi.getRevenueByProvince(params),
        revenueApi.getRevenueByDealType(params),
        revenueApi.getRevenueByPaymentMethod(params),
        revenueApi.getRevenueBySales(params),
        revenueApi.getRevenueByProductType(params),
        revenueApi.getVoucherStats(params),
        revenueApi.getRevenueTrend({ ...params, groupBy: trendGroupBy }),
      ]);

      set({
        revenueByStudio,
        revenueByProvince,
        revenueByDealType,
        revenueByPaymentMethod,
        revenueBySales,
        revenueByProductType,
        voucherStats,
        revenueTrend,
        isLoading: false,
        fetchedKey: currentKey,
        reportFrom: fromDate,
        reportTo: toDate,
      });
    } catch (error: any) {
      set({ error: error.message || 'Failed to fetch revenue data', isLoading: false });
    }
  },
}));
